import React, { useEffect, useState } from 'react';
import './App.css';

interface Site {
  name: string;
  url: string;
  dashUrl: string;
  type: 'worker' | 'pages';
  accessControl: boolean;
  lastChanged: string | null;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function App() {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/sites')
      .then((r) => r.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setSites(data.sites ?? []);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="App">
      <header className="App-header">
        <img
          src="https://www.not404.uk/circle.png"
          className="App-logo"
          alt="not404"
        />
        <h1 className="App-title">not404.uk – status</h1>
      </header>

      <main className="App-main">
        {loading && <p className="App-loading">Loading…</p>}
        {error && <p className="App-error">{error}</p>}
        {!loading && !error && sites.length === 0 && (
          <p className="App-empty">No sites found.</p>
        )}
        {!loading && !error && sites.length > 0 && (
          <ul className="Site-list">
            {sites.map((site) => (
              <li key={site.url} className="Site-item">
                <span className={`Site-type Site-type--${site.type}`}>
                  {site.type}
                </span>
                <div className="Site-info">
                  <a
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="Site-url"
                  >
                    {site.url.replace(/^https?:\/\//, '')}
                  </a>
                  {site.lastChanged && (
                    <span className="Site-date" title={new Date(site.lastChanged).toLocaleString()}>
                      {timeAgo(site.lastChanged)}
                    </span>
                  )}
                </div>
                <a
                  href={site.dashUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="Site-dash"
                  title="Open in Cloudflare dashboard"
                >
                  <svg viewBox="0 0 64 64" width="14" height="14" aria-hidden="true">
                    <path fill="#f6821f" d="M43.2 41.3c.3-.9.2-1.8-.3-2.5-.4-.6-1.1-1-1.9-1.1l-21.4-.3c-.2 0-.3-.1-.4-.2-.1-.1-.1-.3 0-.5.1-.3.4-.5.7-.5l21.6-.3c2.6-.1 5.4-2.2 6.3-4.7l1.2-3.2c.1-.2.1-.4 0-.6C47 23 42.3 19 36.6 19c-4.9 0-9.1 2.9-11.1 7.1-.9-.7-2.1-1.1-3.4-1-2.4.2-4.3 2.2-4.5 4.6 0 .4 0 .8.1 1.2C14.3 31.1 11 34.6 11 38.8c0 .4 0 .8.1 1.2 0 .2.2.4.4.4H42c.3 0 .6-.2.7-.5l.5-1.2v2.6z"/>
                  </svg>
                </a>
                <span
                  className={`Site-access ${site.accessControl ? 'Site-access--on' : 'Site-access--off'}`}
                >
                  {site.accessControl ? 'Access' : 'Public'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

export default App;
